import React, { useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { designSystem, reactHookForm } from '@yola/ws-ui';
import { utils } from '@yola/ws-sdk';
import constants from '../constants';
import { createKeywordOption } from '../helpers/get-keywords-list';
import validateKeywords from '../helpers/validate-keywords';
import validateCanonicalUrl from '../helpers/validate-canonical-url';
import SearchPreview from './search-preview';

const {
  Stack,
  Heading,
  ControlGroup,
  CheckboxGroup,
  Mark,
  InputGroupField,
  CheckboxField,
  TextAreaField,
  TagInputField,
  FieldStatus,
} = designSystem;
const { useWatch, useFormState } = reactHookForm;

const { fieldNames } = constants;

function SeoSection({
  control,
  triggerValidation,
  fieldRefs,
  captions,
  pathPrefix,
  businessName,
  isBusinessInfoInWebsiteSettingsFeatureFlagEnabled,
}) {
  const hasUserChangedKeywordsRef = useRef(false);

  const handleKeywordsChange = useCallback(() => {
    hasUserChangedKeywordsRef.current = true;
    triggerValidation(fieldNames.KEYWORDS);
  }, [triggerValidation]);

  const [
    pageTitleValue,
    pagePathValue,
    pageDescriptionValue,
    usePageNameForPageTitle,
    useBusinessNameInPageTitle,
    usePageUrlForCanonicalUrl,
  ] = useWatch({
    control,
    name: [
      fieldNames.TITLE,
      fieldNames.PATH,
      fieldNames.DESCRIPTION,
      fieldNames.USE_PAGE_NAME_FOR_PAGE_TITLE,
      fieldNames.USE_BUSINESS_NAME_IN_PAGE_TITLE,
      fieldNames.USE_PAGE_URL_FOR_CANONICAL_URL,
    ],
  });

  const { errors } = useFormState({
    control,
    name: [fieldNames.KEYWORDS, fieldNames.CANONICAL_URL],
  });
  const hasKeywordsError = Boolean(errors[fieldNames.KEYWORDS]);
  const hasCanonicalUrlError = Boolean(errors[fieldNames.CANONICAL_URL]);

  let previewTitle = pageTitleValue;
  const path = `${pathPrefix}${pagePathValue}`;
  const description = pageDescriptionValue?.trim()
    ? pageDescriptionValue
    : captions.searchPreview.description;

  if (
    isBusinessInfoInWebsiteSettingsFeatureFlagEnabled &&
    businessName &&
    useBusinessNameInPageTitle
  ) {
    const unescapedBusinessName = utils.unescapeHTML(businessName);

    if (previewTitle) {
      previewTitle = `${previewTitle} | ${unescapedBusinessName}`;
    } else {
      previewTitle = unescapedBusinessName;
    }
  }

  return (
    <Stack gap="spacing-m">
      <Stack gap="spacing-3-xs">
        <Heading type="heading-6">{captions.searchPreview.heading}</Heading>
        <SearchPreview
          title={previewTitle || captions.searchPreview.title}
          path={path}
          description={description}
        />
      </Stack>

      <ControlGroup title={captions.title.name}>
        <div ref={fieldRefs[fieldNames.TITLE]} className="ws-page-settings__field-wrapper">
          <InputGroupField
            name={fieldNames.TITLE}
            control={control}
            disabled={usePageNameForPageTitle}
          />
          <FieldStatus text={captions.title.description} />
        </div>
      </ControlGroup>

      <CheckboxGroup>
        <CheckboxField
          name={fieldNames.USE_PAGE_NAME_FOR_PAGE_TITLE}
          control={control}
          label={captions.usePageNameForPageTitle.label}
        />

        {isBusinessInfoInWebsiteSettingsFeatureFlagEnabled && (
          <CheckboxField
            name={fieldNames.USE_BUSINESS_NAME_IN_PAGE_TITLE}
            control={control}
            label={captions.useBusinessNameInPageTitle.label}
            description={
              !businessName && (
                <>
                  {captions.useBusinessNameInPageTitle.description}
                  <Mark appearance="high-emphasis">
                    {captions.useBusinessNameInPageTitle.settingsTitle} &#x2192;{' '}
                    {captions.useBusinessNameInPageTitle.businessInfoTitle}
                  </Mark>
                </>
              )
            }
            disabled={!businessName}
          />
        )}
      </CheckboxGroup>

      <CheckboxField
        name={fieldNames.PREVENT_INDEXING}
        control={control}
        label={captions.preventIndexing.name}
        description={captions.preventIndexing.description}
      />

      <ControlGroup title={captions.description.name}>
        <div ref={fieldRefs[fieldNames.DESCRIPTION]} className="ws-page-settings__field-wrapper">
          <TextAreaField name={fieldNames.DESCRIPTION} control={control} resize />
          <FieldStatus text={captions.description.description} />
        </div>
      </ControlGroup>

      <ControlGroup title={captions.keywords.name}>
        <div ref={fieldRefs[fieldNames.KEYWORDS]} className="ws-page-settings__field-wrapper">
          <TagInputField
            name={fieldNames.KEYWORDS}
            control={control}
            rules={{
              validate: (value) =>
                hasUserChangedKeywordsRef.current ? validateKeywords(value) : null,
            }}
            isTagTextClipped={false}
            shouldCreateTagOnPaste
            onCreateOption={createKeywordOption}
            onChange={handleKeywordsChange}
          />
          {!hasKeywordsError && <FieldStatus text={captions.keywords.description} />}
        </div>
      </ControlGroup>

      <Stack gap="spacing-3-xs">
        <ControlGroup title={captions.canonicalUrl.name}>
          <div
            ref={fieldRefs[fieldNames.CANONICAL_URL]}
            className="ws-page-settings__field-wrapper"
          >
            <InputGroupField
              name={fieldNames.CANONICAL_URL}
              control={control}
              iconGlyph="link"
              placeholder="https://"
              disabled={usePageUrlForCanonicalUrl}
              rules={{
                validate: (value) => {
                  if (usePageUrlForCanonicalUrl || !value?.trim()) {
                    return null;
                  }
                  return validateCanonicalUrl(value);
                },
              }}
            />
            {!hasCanonicalUrlError && <FieldStatus text={captions.canonicalUrl.description} />}
          </div>
        </ControlGroup>

        <CheckboxField
          name={fieldNames.USE_PAGE_URL_FOR_CANONICAL_URL}
          control={control}
          label={captions.sameAsPageUrl.label}
        />
      </Stack>
    </Stack>
  );
}

SeoSection.propTypes = {
  control: PropTypes.shape().isRequired,
  triggerValidation: PropTypes.func.isRequired,
  fieldRefs: PropTypes.shape().isRequired,
  captions: PropTypes.shape().isRequired,
  pathPrefix: PropTypes.string.isRequired,
  businessName: PropTypes.string.isRequired,
  isBusinessInfoInWebsiteSettingsFeatureFlagEnabled: PropTypes.bool.isRequired,
};

export default SeoSection;
